/** Bump when consent shape/behavior changes so stale browser state is ignored. */
export const CONSENT_STORAGE_KEY = 'bg_prefs_v1';
export const CONSENT_LOG_KEY = 'bg_prefs_log_v1';
const LEGACY_CONSENT_KEYS = [
	'bg_cookie_consent_v1',
	'bg_cookie_consent_v2',
	'bg_cookie_consent_v3',
	'bg_cookie_consent_log_v3'
] as const;

export const CONSENT_OPEN_EVENT = 'bg:prefs-open';
export const CONSENT_POLICY_VERSION = '2026-10-17';

export type ConsentSource = 'accept_all' | 'reject_all' | 'custom' | 'gpc' | 'do_not_sell';

export type ConsentOpenDetail = {
	/** Why the banner was reopened from footer / policy links. */
	reason?: 'settings' | 'do_not_sell';
};

export type ConsentChoice = {
	analytics: boolean;
	decidedAt: string;
	source: ConsentSource;
	gpc: boolean;
	policyVersion: string;
	path: string;
};

export type ConsentRegionMode = 'eu' | 'us' | 'other';

const CLARITY_ID = 'vbbsyiqkm3';
const GA_ID = 'G-2NF2M8KH59';
const MAX_LOG_ENTRIES = 25;

export function isBrowser(): boolean {
	return typeof window !== 'undefined';
}

export function purgeLegacyConsent(): void {
	if (!isBrowser()) return;
	for (const key of LEGACY_CONSENT_KEYS) {
		try {
			window.localStorage.removeItem(key);
		} catch {
			/* ignore */
		}
	}
}

export function hasGpcSignal(): boolean {
	if (!isBrowser()) return false;
	try {
		return window.navigator.globalPrivacyControl === true;
	} catch {
		return false;
	}
}

/** Best-effort region hint for banner copy (not a legal geo IP lookup). */
export function detectConsentRegionMode(): ConsentRegionMode {
	if (!isBrowser()) return 'other';

	try {
		const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
		if (tz === 'Europe/London' || tz === 'Europe/Dublin') return 'eu';
		if (tz.startsWith('Europe/')) return 'eu';
		if (tz.startsWith('America/')) return 'us';

		const lang = (navigator.language || '').toLowerCase();
		if (lang === 'en-gb' || lang === 'en-ie' || lang.endsWith('-gb') || lang.endsWith('-ie')) {
			return 'eu';
		}
		if (
			/^(fr|de|es|it|nl|pl|pt|sv|da|fi|cs|ro|hu|el|bg|hr|sk|sl|lt|lv|et|mt|ga)([-_]|$)/.test(lang)
		) {
			return 'eu';
		}
		if (lang === 'en-us' || lang.startsWith('en-us') || lang.endsWith('-us')) return 'us';
	} catch {
		/* ignore */
	}

	return 'other';
}

export function readConsent(): ConsentChoice | null {
	if (!isBrowser()) return null;
	try {
		const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as Partial<ConsentChoice>;
		if (typeof parsed.analytics !== 'boolean') return null;
		return {
			analytics: parsed.analytics,
			decidedAt: typeof parsed.decidedAt === 'string' ? parsed.decidedAt : new Date().toISOString(),
			source: parsed.source ?? 'custom',
			gpc: parsed.gpc === true,
			policyVersion: parsed.policyVersion ?? CONSENT_POLICY_VERSION,
			path: typeof parsed.path === 'string' ? parsed.path : '/'
		};
	} catch {
		return null;
	}
}

function appendConsentLog(choice: ConsentChoice): void {
	if (!isBrowser()) return;
	try {
		const raw = window.localStorage.getItem(CONSENT_LOG_KEY);
		const prev = raw ? (JSON.parse(raw) as ConsentChoice[]) : [];
		const next = Array.isArray(prev) ? [...prev, choice] : [choice];
		window.localStorage.setItem(CONSENT_LOG_KEY, JSON.stringify(next.slice(-MAX_LOG_ENTRIES)));
	} catch {
		/* ignore */
	}
}

export function writeConsent(choice: ConsentChoice): void {
	if (!isBrowser()) return;
	try {
		window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(choice));
		appendConsentLog(choice);
	} catch {
		/* ignore quota / private mode */
	}
}

export function openConsentPreferences(detail: ConsentOpenDetail = {}): void {
	if (!isBrowser()) return;
	window.dispatchEvent(new CustomEvent<ConsentOpenDetail>(CONSENT_OPEN_EVENT, { detail }));
}

/** Reject analytics (US “Do Not Sell/Share” / opt-out control for this site). */
export function optOutAnalytics(): ConsentChoice {
	return saveAndApplyConsent(false, hasGpcSignal() ? 'gpc' : 'do_not_sell');
}

export function applyGtagConsent(analyticsGranted: boolean): void {
	if (!isBrowser() || typeof window.gtag !== 'function') return;

	try {
		window.gtag('consent', 'update', {
			analytics_storage: analyticsGranted ? 'granted' : 'denied',
			ad_storage: 'denied',
			ad_user_data: 'denied',
			ad_personalization: 'denied'
		});
	} catch {
		/* ignore */
	}
}

function loadGtagScript(): void {
	if (!isBrowser()) return;
	if (document.querySelector('script[data-bg-gtag]')) return;

	const script = document.createElement('script');
	script.async = true;
	script.dataset.bgGtag = '1';
	script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
	document.head.appendChild(script);

	script.addEventListener(
		'load',
		() => {
			try {
				window.gtag?.('js', new Date());
				window.gtag?.('config', GA_ID);
			} catch {
				/* ignore */
			}
		},
		{ once: true }
	);
}

function loadClarityScript(): void {
	if (!isBrowser()) return;
	if (document.querySelector('script[data-bg-clarity]')) return;

	const script = document.createElement('script');
	script.async = true;
	script.dataset.bgClarity = '1';
	script.src = `https://www.clarity.ms/tag/${CLARITY_ID}`;
	document.head.appendChild(script);
}

export function enableAnalyticsVendors(): void {
	if (!isBrowser()) return;
	try {
		applyGtagConsent(true);
		loadGtagScript();
		loadClarityScript();

		const notifyClarity = (): void => {
			try {
				if (typeof window.clarity === 'function') {
					window.clarity('consent');
				}
			} catch {
				/* ignore */
			}
		};
		notifyClarity();
		window.setTimeout(notifyClarity, 400);
		window.setTimeout(notifyClarity, 1200);
	} catch {
		/* ignore */
	}
}

export function disableAnalyticsVendors(): void {
	if (!isBrowser()) return;
	try {
		applyGtagConsent(false);
		if (typeof window.clarity === 'function') {
			window.clarity('consent', false);
		}
	} catch {
		/* ignore */
	}
}

export function applyAnalyticsConsent(analyticsGranted: boolean): void {
	try {
		if (analyticsGranted && !hasGpcSignal()) {
			enableAnalyticsVendors();
		} else {
			disableAnalyticsVendors();
		}
	} catch {
		/* never let analytics break the app */
	}
}

export function trackPageView(pathname: string): void {
	if (!isBrowser()) return;
	if (hasGpcSignal()) return;
	const consent = readConsent();
	if (!consent?.analytics) return;

	try {
		if (typeof window.gtag === 'function') {
			window.gtag('config', GA_ID, { page_path: pathname });
		}
		if (typeof window.clarity === 'function') {
			window.clarity('set', 'page', window.location.href);
		}
	} catch {
		/* ignore */
	}
}

export function saveAndApplyConsent(
	analytics: boolean,
	source: ConsentSource = 'custom'
): ConsentChoice {
	const gpc = hasGpcSignal();
	const granted = analytics && !gpc;
	const choice: ConsentChoice = {
		analytics: granted,
		decidedAt: new Date().toISOString(),
		source: gpc && !analytics ? 'gpc' : source,
		gpc,
		policyVersion: CONSENT_POLICY_VERSION,
		path: isBrowser() ? window.location.pathname : '/'
	};
	writeConsent(choice);
	applyAnalyticsConsent(granted);
	return choice;
}

/**
 * Resolve initial analytics state:
 * - GPC → always deny (US state laws)
 * - stored choice → honor it (unless GPC)
 * - otherwise → deny until opt-in (EU ePrivacy default for this site)
 */
export function bootstrapConsent(): {
	choice: ConsentChoice | null;
	shouldShowBanner: boolean;
	regionMode: ConsentRegionMode;
} {
	purgeLegacyConsent();
	const regionMode = detectConsentRegionMode();
	const gpc = hasGpcSignal();

	if (gpc) {
		const choice = saveAndApplyConsent(false, 'gpc');
		return { choice, shouldShowBanner: false, regionMode };
	}

	const existing = readConsent();
	if (existing) {
		applyAnalyticsConsent(existing.analytics);
		return { choice: existing, shouldShowBanner: false, regionMode };
	}

	disableAnalyticsVendors();
	return { choice: null, shouldShowBanner: true, regionMode };
}
